import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, MapPin, Filter, Star, Car, ShieldCheck, ArrowRight,
  CheckCircle2, MessageSquare, ChevronDown, Loader2, Lock, Globe, Megaphone,
  WifiOff,
} from "lucide-react";
import DemoBadge from "../components/DemoBadge";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import useOnlineStatus from "@/hooks/useOnlineStatus";
import {
  ACTIVE_SERVICE_TYPES, COMING_SOON_SERVICE_TYPES, COMING_SOON_MESSAGE,
} from "../lib/serviceTypes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Backend business_type values used by the older demo data. Map active chip
// values onto the backend filter so we don't break existing companies.
const SERVICE_TYPE_BACKEND_MAP = {
  car_rental:                ["car_rental"],
  motorbike_rental:          ["motorbike_rental", "motor_rental", "scooter_rental"],
  car_and_motorbike_rental:  ["car_and_motorbike_rental", "mixed_fleet"],
};

const BUSINESS_TYPES = [
  { value: "",                            label: "All rental types" },
  { value: "car_rental",                  label: "Cars" },
  { value: "motorbike_rental",            label: "Motorbikes / scooters" },
  { value: "car_and_motorbike_rental",    label: "Car & motorbike" },
];

const SERVICE_MODES = [
  { value: "",            label: "Any service" },
  { value: "self_drive",  label: "Self-drive" },
  { value: "with_driver", label: "With driver" },
];

const SORTS = [
  { value: "recommended", label: "Recommended" },
  { value: "rating",      label: "Highest rated" },
  { value: "completed",   label: "Most completed bookings" },
  { value: "online",      label: "Online booking available" },
  { value: "newest",      label: "Newest" },
];

const STATUS_BADGE_TONES = {
  "Online booking available":   "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Booking requests accepted":  "bg-blue-100 text-blue-800 border-blue-200",
  "Uses ConfirmPH":             "bg-slate-100 text-slate-800 border-slate-200",
  "Contact only":               "bg-amber-100 text-amber-800 border-amber-200",
};

/**
 * `demoItems`/`onSelectCompany`/`onFilterInteraction`: optional sandboxed-demo
 * hooks (used by the homepage walkthrough). When `demoItems` is provided,
 * this skips the real `/public/rentals` fetch and never touches
 * `useSearchParams()` (which would otherwise mutate the real address bar
 * even when just embedded, not navigated, on the homepage): filter/search
 * state lives in local `useState` instead, and the card click reports the
 * selection via `onSelectCompany` instead of `navigate()`ing away. Real
 * behavior (real fetch, real URL-synced filters, real navigation) is
 * unchanged when these props are absent.
 */
export default function DiscoverRentals({ demoItems = null, onSelectCompany = null, onFilterInteraction = null } = {}) {
  const isDemo = !!demoItems;
  const [params, setParams] = useSearchParams();
  const [localParams, setLocalParams] = useState(new URLSearchParams());
  const [items, setItems] = useState(demoItems || []);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [loading, setLoading] = useState(!isDemo);
  const [showFilters, setShowFilters] = useState(false);

  const activeParams = isDemo ? localParams : params;
  const q = activeParams.get("q") || "";
  const city = activeParams.get("city") || "";
  const businessType = activeParams.get("type") || "";
  const onlineOnly = activeParams.get("online") === "1";
  const sort = activeParams.get("sort") || "recommended";
  const minRating = activeParams.get("min_rating") || "";
  const serviceMode = activeParams.get("mode") || "";

  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchFailed(false);
      const url = new URL(`${API}/public/rentals`);
      if (q) url.searchParams.set("q", q);
      if (city) url.searchParams.set("city", city);
      if (businessType) url.searchParams.set("business_type", businessType);
      if (onlineOnly) url.searchParams.set("online_booking", "true");
      if (minRating) url.searchParams.set("min_rating", minRating);
      if (serviceMode) url.searchParams.set("service_mode", serviceMode);
      url.searchParams.set("sort", sort);
      try {
        const r = await axios.get(url.toString());
        if (!cancelled) setItems(r.data.items || []);
      } catch {
        // Never fabricate/reuse stale rental data on failure: show an
        // honest "couldn't load" state instead of pretending there are
        // zero matching rentals.
        if (!cancelled) { setItems([]); setFetchFailed(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, q, city, businessType, onlineOnly, sort, minRating, serviceMode]);

  const update = (k, v) => {
    if (isDemo) {
      const next = new URLSearchParams(localParams);
      if (v === "" || v === null || v === undefined || v === false) next.delete(k);
      else next.set(k, v === true ? "1" : String(v));
      setLocalParams(next);
      onFilterInteraction?.(k, v);
      return;
    }
    const next = new URLSearchParams(params);
    if (v === "" || v === null || v === undefined || v === false) next.delete(k);
    else next.set(k, v === true ? "1" : String(v));
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="rentals-discovery-page">
      <SEO
        title="Find Car and Motorbike Rentals in the Philippines"
        description="Browse car and motorbike rental businesses using ConfirmPH booking confirmation and rental management tools."
        canonical="/rentals"
      />
      <section className="bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> Discover Rentals
            </div>
            <h1 className="mt-4 text-3xl sm:text-5xl font-display font-bold text-slate-900">
              Find rental businesses using ConfirmPH
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Search car, motor, van, and scooter rental businesses. Check contact details, booking options,
              customer ratings, and whether online booking is available.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              ConfirmPH helps businesses and customers confirm booking details. ConfirmPH does not own or operate the rental businesses listed.
            </p>
            <p className="mt-3 text-xs text-slate-600">
              Want extra confidence on a specific booking? <Link to="/pricing" data-testid="discover-cta-pricing" className="underline text-emerald-700 hover:text-emerald-800">See renter pricing →</Link>
            </p>
          </div>

          <AdSlot placement="discover_top" className="mt-4" />

          {/* Service-type chips */}
          <div className="mt-6" data-testid="rentals-service-chips">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">
              Service type
            </div>
            <div className="flex flex-wrap gap-1.5">
              <ServiceChip
                active={businessType === ""}
                onClick={() => update("type", "")}
                testId="rentals-chip-all"
              >
                All
              </ServiceChip>
              {ACTIVE_SERVICE_TYPES.map((s) => (
                <ServiceChip
                  key={s.value}
                  active={businessType === s.value}
                  onClick={() => update("type", s.value)}
                  testId={`rentals-chip-${s.value}`}
                >
                  {s.label}
                </ServiceChip>
              ))}
              {COMING_SOON_SERVICE_TYPES.map((s) => (
                <ServiceChip
                  key={s.value}
                  disabled
                  onClick={() => toast.message(COMING_SOON_MESSAGE)}
                  testId={`rentals-chip-${s.value}-disabled`}
                >
                  <Lock className="w-3 h-3 inline -mt-0.5 mr-1" />
                  {s.label}
                </ServiceChip>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              ConfirmPH is currently focused on car and motorbike rentals first. More rental categories are planned.
            </p>
          </div>

          {/* Search row */}
          <div className="mt-8 grid sm:grid-cols-12 gap-2">
            <div className="sm:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                value={q}
                onChange={(e) => update("q", e.target.value)}
                placeholder="Search by business name…"
                data-testid="rentals-search-name"
                className="w-full pl-9 pr-3 py-3 rounded-lg border border-slate-300 bg-white focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15"
              />
            </div>
            <div className="sm:col-span-4 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                value={city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="City or area (e.g. Dumaguete)"
                data-testid="rentals-search-city"
                className="w-full pl-9 pr-3 py-3 rounded-lg border border-slate-300 bg-white focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              data-testid="rentals-filters-toggle"
              className="sm:col-span-3 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium text-sm"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? "Hide filters" : "More filters"}
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>

          {showFilters && (
            <div className="mt-3 grid sm:grid-cols-12 gap-2" data-testid="rentals-filters-panel">
              <select value={businessType} onChange={(e) => update("type", e.target.value)}
                       data-testid="rentals-filter-type"
                       className="sm:col-span-3 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm">
                {BUSINESS_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select value={serviceMode} onChange={(e) => update("mode", e.target.value)}
                       data-testid="rentals-filter-service-mode" aria-label="Service mode"
                       className="sm:col-span-2 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm">
                {SERVICE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select value={minRating} onChange={(e) => update("min_rating", e.target.value)}
                       data-testid="rentals-filter-rating"
                       className="sm:col-span-2 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm">
                <option value="">Any rating</option>
                <option value="3">3.0★ +</option>
                <option value="4">4.0★ +</option>
                <option value="4.5">4.5★ +</option>
              </select>
              <select value={sort} onChange={(e) => update("sort", e.target.value)}
                       data-testid="rentals-filter-sort"
                       className="sm:col-span-3 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm">
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <label className="sm:col-span-2 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm cursor-pointer">
                <input type="checkbox" data-testid="rentals-filter-online-only"
                        checked={onlineOnly} onChange={(e) => update("online", e.target.checked)} />
                <span>Online booking only</span>
              </label>
            </div>
          )}

          <p className="mt-3 text-xs text-slate-500" data-testid="rentals-ranking-note">
            Recommended order considers ratings and reviews, booking activity, profile completeness,
            available booking options, a small plan-level adjustment that applies only alongside
            those quality signals, and a bounded effect from legal-readiness evidence ConfirmPH has
            reviewed. Businesses with Enhanced Vehicle Safety active are listed first among otherwise
            matching results. Sponsored placements are separate and labelled; paying for them never
            changes a verification, legal-readiness or safety status.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {loading ? (
            <div className="py-20 flex items-center justify-center text-slate-500" data-testid="rentals-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading rentals…
            </div>
          ) : fetchFailed ? (
            <FetchFailedState />
          ) : items.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <div className="text-sm text-slate-600 mb-4" data-testid="rentals-result-count">
                {items.length} {items.length === 1 ? "rental" : "rentals"} found
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rentals-grid">
                {items.map((card) => (
                  <RentalCard key={card.id} card={card} onSelectCompany={onSelectCompany} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function RentalCard({ card, onSelectCompany }) {
  const badgeTone = STATUS_BADGE_TONES[card.status_badge] || "bg-slate-100 text-slate-700 border-slate-200";
  // Enterprise businesses get TWO distinct destinations on the card:
  //   • "View business" → legacy profile at /rentals/:slug?profile=1
  //   • "Go to website" → Enterprise mini-site at /rentals/:slug
  // For everyone else, the whole card behaves like the legacy single link.
  const slug = card.public_slug || card.id;
  const hasWebsite =
    card.is_enterprise && card.public_website_enabled !== false;
  const profilePath = hasWebsite ? `/rentals/${slug}?profile=1`
                                  : `/rentals/${slug}`;
  const websitePath = `/rentals/${slug}`;
  const navigate = useNavigate();
  // Sponsored placement buys visibility only: the click still goes to the
  // exact same profile every other card links to. This just records that a
  // sponsored impression turned into a click-through, fire-and-forget like
  // the existing external-website click tracker.
  const trackSponsoredClick = () => {
    if (!card.sponsored) return;
    axios.post(`${API}/public/rentals/sponsored/${card.id}/click`).catch(() => {});
  };
  const goToProfile = () => {
    trackSponsoredClick();
    if (onSelectCompany) onSelectCompany(card); else navigate(profilePath);
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToProfile}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToProfile(); }
      }}
      data-testid={`rental-card-${card.id}`}
      className={`group rounded-2xl border p-5 hover:shadow-md transition cursor-pointer flex flex-col ${
        card.sponsored
          ? "border-amber-300 bg-amber-50/40 hover:border-amber-400"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {card.public_logo_url && (
            <img src={card.public_logo_url} alt={`${card.name} logo`}
                  className="w-12 h-12 rounded-lg border border-slate-200 object-cover flex-shrink-0"
                  data-testid={`rental-logo-${card.id}`} />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <h3 className="font-display text-lg font-semibold truncate" data-testid={`rental-name-${card.id}`}>{card.name}</h3>
              {card.sponsored && (
                <span data-testid={`rental-sponsored-badge-${card.id}`}
                       title="This business paid for a featured position. It does not affect its rating or reviews."
                       className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-amber-400 bg-amber-100 text-amber-900 font-semibold uppercase tracking-wide">
                  <Megaphone className="w-2.5 h-2.5" /> Sponsored
                </span>
              )}
              {card.is_demo && <DemoBadge variant="default" size="xs" testId={`rental-demo-badge-${card.id}`} />}
              {hasWebsite && (
                <span data-testid={`rental-enterprise-badge-${card.id}`}
                       title="This business has its own ConfirmPH website. This is not a trust or safety signal."
                       className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 font-medium">
                  Has website
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {card.city || "-"}
            </div>
          </div>
        </div>
        {card.rating_avg ? (
          <div className="text-right">
            <div className="flex items-center gap-0.5 text-amber-700 text-sm font-semibold">
              <Star className="w-3.5 h-3.5 fill-amber-500 stroke-amber-500" />
              {card.rating_avg.toFixed(1)}
            </div>
            <div className="text-[10px] text-slate-500">{card.rating_count} review{card.rating_count === 1 ? "" : "s"}</div>
          </div>
        ) : null}
      </div>

      {card.description && (
        <p className="mt-3 text-sm text-slate-600 line-clamp-3">{card.description}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${badgeTone}`}>
          {card.status_badge}
        </span>
        {card.response_label && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
            {card.response_label}
          </span>
        )}
        {card.service_modes?.self_drive && (
          <span data-testid={`rental-mode-self-drive-${card.id}`}
                className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
            Self-drive
          </span>
        )}
        {card.service_modes?.with_driver && (
          <span data-testid={`rental-mode-with-driver-${card.id}`}
                className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
            With driver
          </span>
        )}
        {card.vehicle_categories?.slice(0, 4).map((c) => (
          <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
            <Car className="w-2.5 h-2.5 inline mr-0.5" />{c}
          </span>
        ))}
      </div>

      {card.starting_price_php && (
        <div className="mt-3 text-xs text-slate-600">
          From <span className="font-semibold text-slate-900">₱{card.starting_price_php.toLocaleString()}</span> / day
        </div>
      )}

      {/* Spacer pushes the CTA row to the bottom so cards align nicely in the grid */}
      <div className="flex-1" />

      {hasWebsite ? (
        <div className="mt-4 grid grid-cols-2 gap-2" data-testid={`rental-cta-row-${card.id}`}>
          <Link
            to={profilePath}
            onClick={(e) => { e.stopPropagation(); trackSponsoredClick(); }}
            data-testid={`rental-view-business-${card.id}`}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-[#1B4332]"
          >
            View business <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to={websitePath}
            onClick={(e) => { e.stopPropagation(); trackSponsoredClick(); }}
            data-testid={`rental-go-website-${card.id}`}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg btn-brand text-sm font-medium"
          >
            <Globe className="w-3.5 h-3.5" /> Go to website
          </Link>
        </div>
      ) : (
        <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#1B4332] group-hover:underline">
          View business <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center max-w-2xl mx-auto" data-testid="rentals-empty">
      <CheckCircle2 className="w-7 h-7 text-slate-300 mx-auto" />
      <h3 className="mt-3 font-display text-xl font-semibold">No rentals found for this search yet</h3>
      <p className="mt-2 text-sm text-slate-600">
        You can still request booking confirmation if you already contacted a business.
      </p>
      <div className="mt-5 flex flex-wrap gap-3 justify-center">
        <Link to="/request-confirmation" data-testid="rentals-empty-cta-request"
               className="px-4 py-2 rounded-lg btn-brand font-medium text-sm">
          <MessageSquare className="w-4 h-4 inline mr-1" /> Request Booking Confirmation
        </Link>
        <Link to="/find-booking"
               className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
          Find my booking
        </Link>
      </div>
    </div>
  );
}

function FetchFailedState() {
  const online = useOnlineStatus();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center max-w-2xl mx-auto" data-testid="rentals-fetch-failed">
      <WifiOff className="w-7 h-7 text-slate-300 mx-auto" />
      <h3 className="mt-3 font-display text-xl font-semibold">
        {online ? "Couldn't load rentals right now" : "You're offline"}
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        {online
          ? "ConfirmPH couldn't reach the server. Please try again in a moment."
          : "Discover Rentals needs an internet connection to show live listings."}
      </p>
      <div className="mt-5 flex flex-wrap gap-3 justify-center">
        <button type="button" onClick={() => window.location.reload()}
                data-testid="rentals-fetch-failed-retry"
                className="px-4 py-2 rounded-lg btn-brand font-medium text-sm">
          Retry
        </button>
        <Link to="/" className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
          Go to Home
        </Link>
      </div>
    </div>
  );
}

function ServiceChip({ active, disabled, onClick, testId, children }) {
  const base = "inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium transition";
  if (disabled) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        title="Coming soon. ConfirmPH is starting with car and motorbike rentals first."
        className={`${base} border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed hover:bg-slate-100`}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`${base} ${
        active
          ? "border-emerald-600 bg-emerald-50 text-emerald-800"
          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
